/**
 * POS catalog quick picks: popular + recent category/item from the last N clicks.
 * Category clicks are recorded when a category filter is applied; products when
 * a line item is added to the cart.
 */

export const RECENT_SELECTIONS_STORAGE_KEY = "recentSelections";

/** Click history length used for popular counts and recent picks. */
export const MAX_CLICK_HISTORY = 50;

export const MAX_POPULAR_QUICK_PICKS = 2;

/**
 * @typedef {{ kind: 'category', label: string } | { kind: 'product', id: string, title: string }} ClickEvent
 */

/**
 * @typedef {{
 *   popularCategories: { label: string }[],
 *   popularProducts: { id: string, title: string }[],
 *   recentCategory: { label: string } | null,
 *   recentProduct: { id: string, title: string } | null,
 * }} PosQuickPicks
 */

function isValidEvent(e) {
  if (!e || typeof e !== "object") return false;
  if (e.kind === "category") {
    return typeof e.label === "string" && e.label.trim() !== "";
  }
  if (e.kind === "product") {
    return (
      typeof e.id === "string" &&
      e.id.trim() !== "" &&
      typeof e.title === "string" &&
      e.title.trim() !== ""
    );
  }
  return false;
}

function parseStored(data) {
  if (!data || typeof data !== "object") {
    return { events: [] };
  }
  const rawEvents = Array.isArray(data.events)
    ? data.events
    : Array.isArray(data.recent)
      ? data.recent
      : [];
  const events = rawEvents.filter(isValidEvent).slice(0, MAX_CLICK_HISTORY);
  return { events };
}

function writeEvents(state) {
  sessionStorage.setItem(RECENT_SELECTIONS_STORAGE_KEY, JSON.stringify({ events: state.events }));
}

/** @returns {{ events: ClickEvent[] }} */
export function readClickHistory() {
  try {
    const raw = sessionStorage.getItem(RECENT_SELECTIONS_STORAGE_KEY);
    if (!raw) return { events: [] };
    const data = JSON.parse(raw);
    const parsed = parseStored(data);
    const needsMigrate = !Array.isArray(data.events) && Array.isArray(data.recent);
    const needsTrim = Array.isArray(data.events) && data.events.length > parsed.events.length;
    if (needsMigrate || needsTrim) {
      writeEvents(parsed);
    }
    return parsed;
  } catch {
    return { events: [] };
  }
}

/** @returns {PosQuickPicks} */
export function readPosQuickPicks() {
  const { events } = readClickHistory();

  let recentCategory = null;
  let recentProduct = null;
  const categoryStats = new Map();
  const productStats = new Map();

  events.forEach((e, index) => {
    if (e.kind === "category") {
      if (!recentCategory) {
        recentCategory = { label: e.label };
      }
      const key = e.label.trim().toLowerCase();
      const prev = categoryStats.get(key);
      categoryStats.set(key, {
        label: e.label,
        count: (prev?.count || 0) + 1,
        lastIndex: index,
      });
    } else if (e.kind === "product") {
      const id = String(e.id).trim();
      if (!recentProduct) {
        recentProduct = { id, title: e.title };
      }
      const prev = productStats.get(id);
      productStats.set(id, {
        id,
        title: e.title,
        count: (prev?.count || 0) + 1,
        lastIndex: index,
      });
    }
  });

  const pickTopPopular = (statsMap, pickEntry, limit = MAX_POPULAR_QUICK_PICKS) =>
    [...statsMap.values()]
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.lastIndex - b.lastIndex;
      })
      .slice(0, limit)
      .map(pickEntry);

  return {
    popularCategories: pickTopPopular(categoryStats, (s) => ({ label: s.label })),
    popularProducts: pickTopPopular(productStats, (s) => ({ id: s.id, title: s.title })),
    recentCategory,
    recentProduct,
  };
}

/** @deprecated Use readPosQuickPicks */
export function readRecentSelections() {
  return readPosQuickPicks();
}

function appendEvent(event) {
  const prev = readClickHistory();
  const events = [event, ...prev.events].slice(0, MAX_CLICK_HISTORY);
  const next = { events };
  writeEvents(next);
  return next;
}

function recordCategoryClick(label) {
  const trimmed = String(label).trim();
  if (!trimmed) return null;
  return appendEvent({ kind: "category", label: trimmed });
}

function recordProductClick(product) {
  const id = String(product.id ?? product.productId ?? "").trim();
  const title = String(product.title ?? product.name ?? "").trim();
  if (!id || !title) return null;
  return appendEvent({ kind: "product", id, title });
}

/**
 * @param {object|null} item - line item added to cart
 * @param {object|null|undefined} fallbackProduct - catalog row when ids are missing on the line item
 */
export function recordProductSelection(item, fallbackProduct) {
  if (!item || typeof item !== "object") return null;
  const id = item.productId ?? item.id ?? fallbackProduct?.id ?? fallbackProduct?.productId;
  const rawTitle = item.title ?? item.name ?? fallbackProduct?.title ?? fallbackProduct?.name;
  const title = rawTitle != null && String(rawTitle).trim() !== "" ? String(rawTitle).trim() : "";
  if (!id || !title) return null;
  return recordProductClick({ id: String(id), title });
}

export function recordCategorySelection(q) {
  return recordCategoryClick(q);
}
